import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { helpPosition } from './help-position';
import './help-tip.css';

export function HelpTip({ label, children }: { label: string; children: ReactNode }) {
  const id = useId(), [open, setOpen] = useState(false), [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null), panel = useRef<HTMLDivElement>(null);
  const hovering = useRef(false), timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  function cancelClose() { if (timeout.current !== null) { clearTimeout(timeout.current); timeout.current = null; } }
  function enter(pointerType: string) {
    if (pointerType === 'touch') return;
    hovering.current = true; cancelClose(); setOpen(true);
  }
  function leave() {
    hovering.current = false; cancelClose();
    timeout.current = setTimeout(() => {
      timeout.current = null;
      if (!hovering.current && document.activeElement !== trigger.current) setOpen(false);
    }, 150);
  }
  useEffect(() => () => cancelClose(), []);
  useLayoutEffect(() => {
    if (!open) { setPosition(null); return; }
    function update() {
      if (!trigger.current || !panel.current) return;
      setPosition(helpPosition(trigger.current.getBoundingClientRect(), panel.current.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight }));
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
  }, [open, label]);
  useEffect(() => {
    if (!open) return;
    function dismiss(event: PointerEvent) {
      if (!trigger.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) { cancelClose(); setOpen(false); }
    }
    function escape(event: KeyboardEvent) { if (event.key === 'Escape') { cancelClose(); setOpen(false); } }
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape); };
  }, [open]);
  return <span className="help-tip">
    <button ref={trigger} type="button" className="help-tip__trigger" aria-label={label} aria-expanded={open} aria-controls={open ? id : undefined} aria-describedby={open ? id : undefined}
      onPointerEnter={event => enter(event.pointerType)} onPointerLeave={leave}
      onFocus={event => { if (event.currentTarget.matches(':focus-visible')) { cancelClose(); setOpen(true); } }}
      onBlur={leave} onClick={() => { cancelClose(); setOpen(value => !value); }}>?</button>
    {open && typeof document !== 'undefined' && createPortal(<div ref={panel} id={id} role="tooltip" className="help-tip__content"
      style={{ left: position?.left ?? 16, top: position?.top ?? 16, visibility: position ? 'visible' : 'hidden' }}
      onPointerEnter={event => enter(event.pointerType)} onPointerLeave={leave}>
      <strong className="help-tip__title">{label}</strong>{children}
    </div>, document.body)}
  </span>;
}
